'use client';

// Core
import { FC, useEffect, useState } from 'react';

// UI components
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

// Modular context
import { useProductContext } from '@/contexts/product-context';

// Translations
import { useTranslations } from '@/hooks/useTranslations';

// Local components
import { EditProductForm } from './edit-product-form';

// Types
import { TProductData } from '@/types/product';
import { getProducts } from '@/actions';

export const EditProductPopup: FC = () => {
  const { dispatch, state } = useProductContext();

  const editProduct = useTranslations('EditProductPopup');

  const [products, setProducts] = useState<TProductData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        setProducts(Array.isArray(data.products) ? data.products : []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    if (state.isEditProductPopupOpen) {
      fetchProducts();
    }
  }, [state.isEditProductPopupOpen]);

  const product = products.find((item) => item.id === state.productId);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && state.productId) {
      dispatch({
        type: 'OPEN_EDIT_PRODUCT_POPUP',
        payload: { productId: state.productId },
      });
    } else {
      dispatch({ type: 'CLOSE_EDIT_PRODUCT_POPUP' });
    }
  };

  if (loading) {
    return (
      <Sheet
        open={state.isEditProductPopupOpen}
        onOpenChange={handleOpenChange}
      >
        <SheetContent className="bg-white dark:bg-black">
          <SheetHeader>
            <SheetTitle>{editProduct('title')}</SheetTitle>
            <SheetDescription>Loading product details...</SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!product) {
    return (
      <Sheet
        open={state.isEditProductPopupOpen}
        onOpenChange={handleOpenChange}
      >
        <SheetContent className="bg-white dark:bg-black">
          <SheetHeader>
            <SheetTitle>{editProduct('title')}</SheetTitle>
            <SheetDescription>Product could not be found.</SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-destructive">
              Error: Product not found
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={state.isEditProductPopupOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="bg-white dark:bg-black">
        <SheetHeader className="text-left">
          <SheetTitle>{editProduct('title')}</SheetTitle>
          <SheetDescription>{editProduct('description')}</SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 p-4">
          <EditProductForm product={product} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
